import classNames from "classnames";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BaseButton } from "../../../../components/Button/BaseButton.tsx";
import { Icon } from "../../../../components/Icon/Icon.tsx";
import { Skeleton } from "../../../../components/Skeleton/Skeleton.tsx";
import { useOnMount } from "../../../../hooks/useOnMount.ts";
import { WORKSPACE_MODELS, WORKSPACES } from "../../../../routes.ts";
import {
  MODEL_TYPE_ICON_NAMES,
  MODEL_TYPE_LABELS,
} from "../../../Board/display.ts";
import { useModels } from "../../../hooks/useModels.ts";
import { useWorkspaceUuid } from "../../../hooks/useWorkspaceUuid.ts";
import type {
  ModelType,
  ModelWithIsCompatibleAndTags,
} from "../../../types.ts";
import { countNumberOfOccurencesPerModelTags } from "../../../utils/countNumberOfOccurencesPerModelTags.ts";
import { ViewLayout } from "../../ViewLayout.tsx";
import { useCreateModel } from "../hooks/useCreateModel.ts";
import { CUSTOM_TAG, POPULAR_TAG } from "./constants.ts";
import { ModelTile } from "./ModelTile.tsx";

export const ModelsView = ({ modelType }: { modelType: ModelType }) => {
  const workspaceUuid = useWorkspaceUuid();
  const { isLoading, models } = useModels();
  const { createModel, isCreateModelLoading } = useCreateModel();

  const [searchText, setSearchText] = useState("");
  const [selectedTag, setSelectedTag] = useState<string | undefined>(
    POPULAR_TAG,
  );

  const navigate = useNavigate();
  const onModelClick = (model: ModelWithIsCompatibleAndTags) => {
    navigate(
      `${WORKSPACES}/${workspaceUuid}/${WORKSPACE_MODELS}/${model.uuid}`,
    );
  };

  const specificTypeModels = useMemo(
    () => models.filter((model) => model.type === modelType),
    [models, modelType],
  );

  const selectedModels = useMemo(
    () =>
      specificTypeModels.filter((model) =>
        selectedTag
          ? selectedTag === CUSTOM_TAG
            ? !model.is_curated
            : model.tags.includes(selectedTag)
          : true,
      ),
    [specificTypeModels, selectedTag],
  );

  const filteredModels = useMemo(
    () =>
      selectedModels.filter((model) =>
        searchText.length > 0
          ? model.name?.toLowerCase().includes(searchText.toLowerCase())
          : true,
      ),
    [searchText, selectedModels],
  );

  const isEmpty = selectedModels.length === 0 && !isLoading;
  const modelTypeLabel = MODEL_TYPE_LABELS[modelType];

  return (
    <ViewLayout
      title={`${modelTypeLabel} library`}
      leftContent={
        <div className="flex-row gap-600 items-center">
          {isLoading ? (
            <div className="flex-row-center gap-400">
              {Array.from({ length: 3 }).map((_, index) => (
                <Skeleton key={index} className="h-400 w-1600 rounded-100" />
              ))}
            </div>
          ) : (
            <ModelTabs
              models={specificTypeModels}
              selectedTag={selectedTag}
              onSelectedTagUpdate={setSelectedTag}
            />
          )}
        </div>
      }
      searchTextProps={{
        searchText,
        setSearchText,
      }}
      emptyStateProps={{
        iconName: MODEL_TYPE_ICON_NAMES[modelType],
        title: modelTypeLabel,
        header: `Create your first ${modelTypeLabel}`,
        description: `Generate endless content with your custom ${modelTypeLabel} model`,
        buttonLabel: `New ${modelTypeLabel}`,
      }}
      creationButtonProps={{
        label: `New ${modelTypeLabel}`,
        onClick: () => createModel({ type: modelType }),
        loading: isCreateModelLoading,
      }}
      isEmpty={isEmpty}
    >
      <div className="w-full grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-200">
        {/* FIXME: This tile button should be removed as it is a duplicate of the top right button */}
        <NewModelTile modelType={modelType} />
        {isLoading
          ? Array.from({ length: 14 }).map((_, index) => (
              <Skeleton key={index} className="h-[220px] w-full rounded-100" />
            ))
          : filteredModels.map((model) => (
              <ModelTile
                key={model.uuid}
                model={model}
                onClick={() => onModelClick(model)}
              />
            ))}
      </div>
    </ViewLayout>
  );
};

const ModelTabs = ({
  models,
  selectedTag,
  onSelectedTagUpdate,
}: {
  models: ModelWithIsCompatibleAndTags[];
  selectedTag: string | undefined;
  onSelectedTagUpdate: (tag: string | undefined) => void;
}) => {
  const modelsCountByTags = useMemo(
    () => countNumberOfOccurencesPerModelTags(models),
    [models],
  );
  const customModelsCount = models.filter((model) => !model.is_curated).length;

  useOnMount(() => {
    if (customModelsCount > 0) {
      onSelectedTagUpdate(CUSTOM_TAG);
    } else if (
      modelsCountByTags.has(POPULAR_TAG) &&
      (modelsCountByTags.get(POPULAR_TAG) ?? 0) > 0
    ) {
      onSelectedTagUpdate(POPULAR_TAG);
    } else {
      onSelectedTagUpdate(undefined);
    }
  });

  return (
    <div className="flex-row gap-200 label-md-semibold text-primary">
      {modelsCountByTags.has(POPULAR_TAG) && (
        <BaseTagFilterButton
          name={`${POPULAR_TAG} (${modelsCountByTags.get(POPULAR_TAG) ?? 0})`}
          isSelected={POPULAR_TAG === selectedTag}
          onClick={() => onSelectedTagUpdate(POPULAR_TAG)}
        />
      )}
      <BaseTagFilterButton
        name={`My models (${customModelsCount})`}
        isSelected={CUSTOM_TAG === selectedTag}
        onClick={() => onSelectedTagUpdate(CUSTOM_TAG)}
      />
      <BaseTagFilterButton
        name={`All (${models.length})`}
        isSelected={selectedTag === undefined}
        onClick={() => onSelectedTagUpdate(undefined)}
      />
      {Array.from(modelsCountByTags.keys())
        .filter((tagName) => tagName !== POPULAR_TAG)
        .sort()
        .map((tagName) => (
          <BaseTagFilterButton
            key={tagName}
            name={`${tagName} (${modelsCountByTags.get(tagName) ?? 0})`}
            isSelected={tagName === selectedTag}
            onClick={() => onSelectedTagUpdate(tagName)}
          />
        ))}
    </div>
  );
};

const NewModelTile = ({ modelType }: { modelType: ModelType }) => {
  const { createModel, isCreateModelLoading: isLoading } = useCreateModel();

  return (
    <BaseButton
      className="rounded-100 group flex-col-center items-center gap-200 bg-input-surface-rest hover:bg-surface-primary-hover"
      style={{ height: 220 }}
      onClick={() => createModel({ type: modelType })}
      loading={isLoading}
    >
      <Icon name="Plus" size="lg" />
      <div className="text-primary label-lg-semibold">
        New {MODEL_TYPE_LABELS[modelType]}
      </div>
    </BaseButton>
  );
};

export const BaseTagFilterButton = ({
  name,
  isSelected,
  onClick,
}: {
  name: string;
  isSelected: boolean;
  onClick: () => void;
}) => (
  <BaseButton
    className={classNames(
      "px-200 py-100 rounded-100 text-center text-primary label-md-semibold",
      isSelected
        ? "bg-surface-primary-active"
        : "hover:bg-surface-primary-hover",
    )}
    onClick={onClick}
  >
    {name}
  </BaseButton>
);
